import { useForkRef } from "@mui/material";
import { TreeViewBaseItem, UseTreeItemStatus } from "@mui/x-tree-view-pro";
import { propEq } from "ramda";
import React, { MouseEventHandler, type PropsWithChildren, useMemo } from "react";
import { useTreeDnD } from "@sinch/components/application/RatelistBuilder/BuilderTree/TreeDnDProvider";
import type { PositionedItemTree } from "@sinch/components/application/RatelistBuilder/BuilderTree/types";
import { useTreeItemFocus } from "@sinch/components/application/RatelistBuilder/hooks/UseTreeItemFocus";
import {
  DropOverlay,
  ProgressDropOverlay,
} from "@sinch/components/application/RatelistBuilder/RateTreeItem/DropOverlay";
import { ItemConnectorLines } from "@sinch/components/application/RatelistBuilder/RateTreeItem/ItemConnectorLines";
import { RateBuilderNodeEnvelope } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RateBuilderNodeEnvelope";
import { RateRowBody } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RowBody/RateRowBody";
import {
  UseTreeItemContentSlotProps,
  UseTreeItemIconContainerSlotProps,
  UseTreeItemLabelSlotProps,
  UseTreeViewExpansionPublicAPI,
} from "@sinch/components/application/RatelistBuilder/RateTreeItem/types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

interface RateBuilderRowProps<T extends TreeViewBaseItem> extends PropsWithChildren {
  isWrapped: boolean;
  item: PositionedItemTree<T>;
  status: UseTreeItemStatus;
  contentProps: UseTreeItemContentSlotProps;
  labelProps: UseTreeItemLabelSlotProps;
  iconContainerProps: UseTreeItemIconContainerSlotProps;
  onItemExpandToggle: UseTreeViewExpansionPublicAPI["setItemExpansion"];
  rootClassName: string;
  onContextMenu?: MouseEventHandler<any>;
}

export const RateBuilderRow = <T extends TreeViewBaseItem>({
  isWrapped,
  item,
  status,
  contentProps,
  labelProps,
  iconContainerProps,
  onItemExpandToggle,
  rootClassName,
  onContextMenu,
  children,
}: RateBuilderRowProps<T>) => {
  const {
    position,
    parent,
    item: { id: itemId, children: itemChildren },
  } = item;
  const { disableNesting } = useRateBuilderSettings();
  // Computed values
  const itemIndex = useMemo(
    () => item.parent?.children?.findIndex(propEq("id", itemId)),
    [item.parent?.children, itemId]
  );

  const isDraggable = useMemo(() => !isWrapped || itemIndex === 0, [isWrapped, itemIndex]);

  const draggableTreeItemId = useMemo(() => (isWrapped ? parent!.id : itemId), [isWrapped, parent, itemId]);
  const { dropOverlayRef, dragged, draggedHandlerRef, draggedElRef, loading } = useTreeDnD({
    treeItemId: isDraggable ? draggableTreeItemId : undefined,
    dropzoneItemId: itemId,
    index: itemChildren?.length ?? 0,
  });

  const setRef = useForkRef(contentProps.ref, draggedElRef);

  useTreeItemFocus({
    itemId,
    elementRef: draggedElRef,
  });

  return (
    <RateBuilderNodeEnvelope className={rootClassName} draggable={false} dragged={dragged}>
      {!disableNesting && <DropOverlay ref={dropOverlayRef} />}
      {loading && <ProgressDropOverlay />}
      <ItemConnectorLines isWrapped={isWrapped} position={position} />

      <RateRowBody
        draggedHandlerRef={draggedHandlerRef}
        iconContainerProps={iconContainerProps}
        isWrapped={isWrapped}
        item={item}
        labelProps={labelProps}
        onContextMenu={onContextMenu}
        onItemExpandToggle={onItemExpandToggle}
        rootRef={setRef}
        status={status}
      >
        {children}
      </RateRowBody>
    </RateBuilderNodeEnvelope>
  );
};
